import React, { useState, useEffect } from 'react';
import { fetchUsers } from '../../../../../Actions/UserActions';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  const loadUsers = async (currentPage, searchTerm) => {
    try {
      setLoading(true);
      const response = await fetchUsers(currentPage, limit, searchTerm);
      setUsers(response.users);
      setTotal(response.total);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeoutId = setTimeout(() => {
      loadUsers(page, search);
    }, 500);

    setSearchTimeout(timeoutId);

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [page, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page when searching
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return '-';

    // 1) Parse input as UTC instant
    let utcMs;
    if (typeof dateInput === 'number') {
      utcMs = dateInput < 1e12 ? dateInput * 1000 : dateInput; // seconds vs ms
    } else {
      let str = String(dateInput).trim();
      if (!str.includes('T') && str.includes(' ')) {
        str = str.replace(' ', 'T');
      }
      // Trim fractional seconds longer than 3 digits to milliseconds precision
      str = str.replace(/\.(\d{3})\d+/, '.$1');

      const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$|GMT/i.test(str);
      const isoNoTimezone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/.test(str);
      const normalized = hasTimezone ? str : (isoNoTimezone ? `${str}Z` : str);
      const parsed = Date.parse(normalized);
      utcMs = Number.isNaN(parsed) ? NaN : parsed;
    }

    if (!Number.isFinite(utcMs)) return '-';

    // 2) Convert to IST by adding +05:30 (330 minutes)
    const IST_OFFSET_MINUTES = 330;
    const istMs = utcMs + IST_OFFSET_MINUTES * 60 * 1000;
    const d = new Date(istMs);

    // 3) Manually format using UTC getters (since we've shifted the epoch)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const year = d.getUTCFullYear();
    const month = months[d.getUTCMonth()];
    const day = d.getUTCDate();
    const minutes = d.getUTCMinutes().toString().padStart(2, '0');
    const hours24 = d.getUTCHours();
    const hours12 = ((hours24 + 11) % 12) + 1;
    const ampm = hours24 >= 12 ? 'PM' : 'AM';

    return `${month} ${day}, ${year}, ${hours12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Users Management</h1>
        <div className="relative">
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={handleSearchChange}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login At</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">{user.full_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{user.mobile}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{formatDate(user.last_login_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className={`px-4 py-2 border rounded-md ${page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * limit >= total}
                  className={`px-4 py-2 border rounded-md ${page * limit >= total ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Users;
